import React, { useState, useEffect } from 'react';
import { Check, Shield, Clock, Zap, ChevronRight, Menu, X, AlertCircle, Users, Award, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import '../styles/Home.css';

const Home = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  const features = [
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Real-Time DDI Detection",
      description: "Instant drug-drug interaction alerts as you prescribe. Our system analyzes every medication combination against comprehensive databases.",
      color: "accent-purple"
    },
    {
      icon: <AlertCircle className="w-8 h-8" />,
      title: "Duplicate Therapy Prevention",
      description: "Automatically identifies duplicate medications and overlapping therapeutic classes to prevent redundant prescribing.",
      color: "accent-blue"
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "FDC Risk Analysis",
      description: "Maps brand names to active ingredients, detecting dangerous fixed-dose combination interactions before they happen.",
      color: "accent-orange"
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: "Custom Templates",
      description: "Create and save prescription templates for common conditions. Write prescriptions 3x faster with drag-and-drop workflows.",
      color: "accent-pink"
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Quick Medicine Selection",
      description: "Intelligent autocomplete and favorites system. Access frequently prescribed medications in seconds.",
      color: "accent-green"
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: "Brand-to-Generic Mapping",
      description: "Comprehensive database linking brand names to active ingredients. Never miss an interaction due to naming confusion.",
      color: "accent-grey"
    },
    {
      icon: <MessageSquare className="w-8 h-8" />,
      title: "Chat-Based Prescription",
      description: "Generate prescriptions through natural conversation. Simply describe symptoms and patient details, and our AI assists you in creating complete, safe prescriptions instantly.",
      color: "accent-pink"
    }
  ];

  const howItWorks = [
    {
      step: "01",
      title: "Integrate Seamlessly",
      description: "Connect Conscious Rx with your existing EMR system or use our standalone platform. Setup takes minutes, not weeks."
    },
    {
      step: "02",
      title: "Prescribe Safely",
      description: "Write prescriptions as usual. Our AI analyzes every drug combination in real-time, alerting you to potential interactions instantly."
    },
    {
      step: "03",
      title: "Save Time & Lives",
      description: "Use custom templates for faster prescribing. Get instant safety feedback. Focus on patient care, not paperwork."
    }
  ];

  const stats = [
    { number: "Pilot Phase", label: "Now Accepting Doctors" },
    { number: "Limited Spots", label: "Early Access Available" },
    { number: "Free Access", label: "For Pilot Participants" },
    { number: "2025", label: "Launch Year" }
  ];

  return (
    <div className="home-container">
      {/* Header Navigation */}
      <header className={`header-nav ${isScrolled ? 'scrolled' : ''}`}>
        <div className="header-content">
          <div className="logo-section">
            <Shield className="logo-icon" />
            <span className="logo-text">Med Conscious</span>
          </div>

          <nav className={`nav-links ${isMenuOpen ? 'open' : ''}`}>
            <button onClick={() => scrollToSection('features')} className="nav-link">Features</button>
            <button onClick={() => scrollToSection('how-it-works')} className="nav-link">How It Works</button>
            <button onClick={() => scrollToSection('testimonials')} className="nav-link">Testimonials</button>
            <button onClick={() => scrollToSection('contact')} className="nav-link">Contact</button>
          </nav>

          <div className="nav-actions">
            <Button variant="ghost" className="btn-secondary" onClick={() => scrollToSection('contact')}>
              Request Demo
            </Button>
            <button className="mobile-menu-toggle" onClick={() => setIsMenuOpen(!isMenuOpen)}>
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-announcement">
            <Shield className="w-4 h-4" />
            <span>Now in Pilot Phase - Join Today</span>
          </div>

          <h1 className="heading-hero">
            Write Prescriptions<br />Faster. Safer. Smarter.
          </h1>

          <p className="hero-subtitle body-large">
            Be among the first doctors to experience real-time drug interaction detection and prescription templates. Join our exclusive pilot program and shape the future of prescription safety.
          </p>

          <div className="hero-cta">
            <Button className="btn-primary" onClick={() => scrollToSection('contact')}>
              Join Pilot Program
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
            <Button variant="ghost" className="btn-secondary" onClick={() => scrollToSection('how-it-works')}>
              Learn More
            </Button>
          </div>

          <div className="hero-stats">
            {stats.map((stat, index) => (
              <div key={index} className="stat-item">
                <div className="stat-number heading-2">{stat.number}</div>
                <div className="stat-label mono-text">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Problem Statement Section */}
      <section className="problem-section">
        <div className="container">
          <div className="problem-content">
            <div className="problem-text">
              <h2 className="heading-1">The Hidden Crisis in Healthcare</h2>
              <p className="body-large">
                Every year, thousands of patients suffer from adverse drug reactions caused by medication interactions.
                Doctors juggle complex brand names, generic formulations, and fixed-dose combinations—often without
                real-time safety checks.
              </p>
              <p className="body-medium">
                We're building the solution. Med Conscious brings AI-powered safety analysis directly into your prescribing workflow.
                Join our pilot program and be part of this transformation from day one.
              </p>
            </div>
            <div className="problem-image">
              <img
                src="https://images.unsplash.com/photo-1758691463606-1493d79cc577"
                alt="Doctor using modern healthcare technology"
                className="rounded-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Conscious Rx Platform Features</h2>
            <div style={{ margin: '1rem 0' }}>
              <Button
                className="btn-primary"
                onClick={() => window.location.href = 'https://consciousrx.medconscious.co.in'}
              >
                Explore Conscious Rx
              </Button>
            </div>
            <p className="body-large">
              Comprehensive prescription safety and efficiency tools designed for modern healthcare providers
            </p>
          </div>

          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className={`feature-card ${feature.color}`}>
                <div className="feature-icon">
                  {feature.icon}
                </div>
                <h3 className="heading-3">{feature.title}</h3>
                <p className="body-medium">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="how-it-works-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">How Conscious Rx Works</h2>
            <p className="body-large">
              Three simple steps to safer, faster prescribing
            </p>
          </div>

          <div className="steps-grid">
            {howItWorks.map((step, index) => (
              <div key={index} className="step-card">
                <div className="step-number mono-text">{step.step}</div>
                <h3 className="heading-3">{step.title}</h3>
                <p className="body-medium">{step.description}</p>
              </div>
            ))}
          </div>

          <div className="workflow-image">
            <img
              src="https://images.unsplash.com/photo-1758691461916-dc7894eb8f94"
              alt="Professional healthcare workflow"
              className="rounded-image"
            />
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section">
        <div className="container">
          <div className="benefits-grid">
            <div className="benefit-column">
              <h2 className="heading-1">Built for Healthcare Professionals</h2>
              <p className="body-large">
                Whether you're a solo practitioner or managing a multi-hospital network,
                Conscious Rx scales with your needs.
              </p>

              <div className="benefit-list">
                <div className="benefit-item">
                  <Check className="check-icon" />
                  <div>
                    <h4 className="heading-3">For Individual Doctors</h4>
                    <p className="body-small">Personal prescription templates, quick access to your most-used medications, and real-time safety alerts tailored to your practice.</p>
                  </div>
                </div>

                <div className="benefit-item">
                  <Check className="check-icon" />
                  <div>
                    <h4 className="heading-3">For Hospitals & Clinics</h4>
                    <p className="body-small">Enterprise dashboards, multi-doctor management, institutional formularies, and comprehensive audit trails for compliance.</p>
                  </div>
                </div>

                <div className="benefit-item">
                  <Check className="check-icon" />
                  <div>
                    <h4 className="heading-3">For Healthcare Systems</h4>
                    <p className="body-small">API integration with existing EMRs, custom safety protocols, and analytics across your entire network.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="benefit-image">
              <img
                src="https://images.unsplash.com/photo-1758691463384-771db2f192b3"
                alt="Healthcare professional using technology"
                className="rounded-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Pilot Program Benefits Section */}
      <section id="testimonials" className="pilot-benefits-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Why Join Our Pilot Program?</h2>
            <p className="body-large">
              Be at the forefront of prescription safety innovation
            </p>
          </div>

          <div className="pilot-benefits-grid">
            <div className="pilot-benefit-card accent-purple">
              <div className="pilot-icon">
                <Shield className="w-10 h-10" />
              </div>
              <h3 className="heading-3">Free Full Access</h3>
              <p className="body-medium">
                Get complete access to all Conscious Rx features at no cost during the pilot phase. Experience the full power of our platform.
              </p>
            </div>

            <div className="pilot-benefit-card accent-blue">
              <div className="pilot-icon">
                <Users className="w-10 h-10" />
              </div>
              <h3 className="heading-3">Shape the Product</h3>
              <p className="body-medium">
                Your feedback directly influences product development. Help us build features that matter most to practicing physicians.
              </p>
            </div>

            <div className="pilot-benefit-card accent-orange">
              <div className="pilot-icon">
                <Award className="w-10 h-10" />
              </div>
              <h3 className="heading-3">Exclusive Benefits</h3>
              <p className="body-medium">
                Early adopters receive lifetime discounted pricing, priority support, and recognition as founding members of our community.
              </p>
            </div>

            <div className="pilot-benefit-card accent-green">
              <div className="pilot-icon">
                <Zap className="w-10 h-10" />
              </div>
              <h3 className="heading-3">Direct Support</h3>
              <p className="body-medium">
                Work directly with our founding team. Get personalized onboarding, training, and dedicated support throughout the pilot.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="heading-1">Join Our Pilot Program Today</h2>
            <p className="body-large">
              Limited spots available for doctors who want to be part of revolutionizing prescription safety.
              Get free access and help shape the future of healthcare.
            </p>

            <div className="cta-form">
              <input
                type="email"
                placeholder="Enter your email address"
                className="cta-input"
              />
              <Button className="btn-primary">
                Apply for Pilot
              </Button>
            </div>

            <p className="cta-note caption">
              Free pilot access • Limited spots • Priority given to active practitioners
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-column">
              <div className="footer-logo">
                <Shield className="w-6 h-6" />
                <span className="logo-text">Med Conscious</span>
              </div>
              <p className="body-small">
                Prescription safety and efficiency platform for modern healthcare providers.
              </p>
            </div>

            <div className="footer-column">
              <h4 className="heading-3">Product</h4>
              <ul className="footer-links">
                <li><button onClick={() => scrollToSection('features')}>Features</button></li>
                <li><button onClick={() => scrollToSection('how-it-works')}>How It Works</button></li>
                <li><button>Pricing</button></li>
                <li><button>API Documentation</button></li>
              </ul>
            </div>

            <div className="footer-column">
              <h4 className="heading-3">Company</h4>
              <ul className="footer-links">
                <li><button>About Us</button></li>
                <li><button>Careers</button></li>
                <li><button>Contact</button></li>
                <li><button>Blog</button></li>
              </ul>
            </div>

            <div className="footer-column">
              <h4 className="heading-3">Legal</h4>
              <ul className="footer-links">
                <li><button>Privacy Policy</button></li>
                <li><button>Terms of Service</button></li>
                <li><button>HIPAA Compliance</button></li>
                <li><button>Security</button></li>
              </ul>
            </div>
          </div>

          <div className="footer-bottom">
            <p className="mono-text">© 2025 Med Conscious. All rights reserved.</p>
            <p className="mono-text">Building safer healthcare, one prescription at a time.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
